export type Format = "bold" | "italic" | "link";

/** Replace `source[start, end)` with `text`, then select `selection`. */
export interface FormatEdit {
  readonly start: number;
  readonly end: number;
  readonly text: string;
  readonly selection: readonly [number, number];
}

/** Toggle emphasis on, or wrap a link around, the selected markdown source. */
export function formatSelection(
  source: string,
  start: number,
  end: number,
  format: Format,
): FormatEdit {
  // Drag selections tend to catch neighbouring spaces or delimiters, and emphasis can't open
  // before a space or close after one.
  while (start < end && /[\s*]/.test(source[start]!)) start++;
  while (end > start && /[\s*]/.test(source[end - 1]!)) end--;
  const selected = source.slice(start, end);

  if (format === "link") {
    if (!selected || /^https?:\/\/\S+$/.test(selected)) {
      return { start, end, text: `[](${selected})`, selection: [start + 1, start + 1] };
    }
    const caret = start + selected.length + 3;
    return { start, end, text: `[${selected}]()`, selection: [caret, caret] };
  }

  // Asterisk runs hugging the selection say what is already applied: two or more is bold, an odd
  // count is italic, so `***both***` can lose either independently.
  let before = 0;
  while (source[start - before - 1] === "*") before++;
  let after = 0;
  while (source[end + after] === "*") after++;
  const depth = Math.min(before, after, 3);
  const underscored = source[start - 1] === "_" && source[end] === "_";
  const marker = format === "bold" ? "**" : "*";
  const applied = format === "bold" ? depth >= 2 : depth % 2 === 1;
  const unwrap = applied ? marker.length : format === "italic" && underscored ? 1 : 0;
  if (unwrap) {
    return {
      start: start - unwrap,
      end: end + unwrap,
      text: selected,
      selection: [start - unwrap, end - unwrap],
    };
  }
  return {
    start,
    end,
    text: marker + selected + marker,
    selection: [start + marker.length, end + marker.length],
  };
}
